import React from 'react';
import {Link} from 'gatsby';
import styled, {css} from 'styled-components';
import {darken} from 'polished';

import {getColors} from '../utils/styling';
import ArrowRight from '../../assets/inline-svg/icons/arrow_right.svg';

const cssForPrimary = ({theme, primary}) => {
  const backgroundColor = getColors(theme).primary;
  const hoverBackgroundColor = darken(0.05, backgroundColor);

  return css`
    background-color: ${backgroundColor};
    color: ${getColors(theme).gray900};

    &:hover {
      background-color: ${hoverBackgroundColor};
    }
  `;
};

const cssForSecondary = ({theme, primary, secondary}) => {
  //If primary is set to true it will override secondary prop
  if (primary || !secondary) {
    return;
  }
  const backgroundColor = getColors(theme).gray600;
  const hoverBackgroundColor = darken(0.05, backgroundColor);
  return css`
    background-color: ${backgroundColor};
    color: ${getColors(theme).gray100};

    &:hover {
      background-color: ${hoverBackgroundColor};
    }
  `;
};

const cssForLight = ({theme, primary, secondary, light}) => {
  //If primary is set to true it will override secondary prop
  if (primary || secondary) {
    return;
  }
  const backgroundColor = '#E8EDED';
  const hoverBackgroundColor = darken(0.05, backgroundColor);
  return css`
    background-color: ${backgroundColor};
    color: #174f49;

    &:hover {
      background-color: ${hoverBackgroundColor};
    }
  `;
};

const cssForAccent = ({theme, primary, secondary, light}) => {
  //If primary is set to true it will override secondary prop
  if (primary || secondary) {
    return;
  }
  const backgroundColor = '#F687B3';
  const hoverBackgroundColor = darken(0.05, backgroundColor);
  return css`
    background-color: ${backgroundColor};
    color: #000000;

    &:hover {
      background-color: ${hoverBackgroundColor};
    }
  `;
};

export const Button = styled.button`
  border: none;
  border-radius: 4px;
  margin: 8px 0;
  padding: ${({large}) => large ? '16px 32px' : '16px 24px'};
  ${({fluid}) => fluid ? css`width: 100%;` : null};
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;

  ${props => cssForPrimary(props)}
  ${props => cssForSecondary(props)}
  ${props => cssForLight(props)}
  ${props => cssForAccent(props)}
`;

const LinkButtonWrapper = styled('div')`
  border: none;
  outline: none;
  background: transparent;
  ${({color}) => color && css`${({theme}) => css`color: ${getColors(theme)[color]}`};`}
  cursor: pointer;
  transition: transform 300ms;

  span {
    margin-right: 6px;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
  }

  svg {
    ${({color}) => color && css`${({theme}) => css`fill: ${getColors(theme)[color]}`};`}
  }

  &:hover {
    transform: translateX(10px);
  }
`;

export const LinkButton = ({href, color, label}) => (
    <a href={href}>
      <LinkButtonWrapper color={color}>
        <span>{label}</span>
        <ArrowRight/>
      </LinkButtonWrapper>
    </a>
);

export const ExternalLinkButton = styled.a`
  border-radius: 4px;
  margin: 8px 0;
  padding: ${({large}) => large ? '16px 32px' : '16px 24px'};
  ${({fluid}) => fluid ? css`width: 100%;` : null};
  outline: none;
  box-sizing: border-box;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  text-align: center;

  ${props => cssForPrimary(props)}
  ${props => cssForSecondary(props)}
  ${props => cssForLight(props)}
  ${props => cssForAccent(props)}
`;

const ScrollButtonStyled = styled.button`
  border: none;
  outline: none;
  background: transparent;
  ${({color}) => color && css`${({theme}) => css`color: ${getColors(theme)[color]}`};`}
  cursor: pointer;
  transition: transform 300ms;

  span {
    margin-right: 6px;
    font-weight: bold;
    font-size: 16px;
    line-height: 24px;
  }

  svg {
    ${({color}) => color && css`${({theme}) => css`fill: ${getColors(theme)[color]}`};`}
  }

  &:hover {
    transform: translateX(10px);
  }
`;

export const ScrollToButton = ({elementRef, color, label}) => {

  const scroll = ref => {
    ref.scrollIntoView({block: 'start', behavior: 'smooth'});
  };

  return (
      <ScrollButtonStyled onClick={() => scroll(elementRef.current)} color={color}>
        <span>{label}</span>
        <ArrowRight/>
      </ScrollButtonStyled>
  );
};
